import type { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'
import { Rows, Row, Empty } from '@/components/page'

// This file used to draw its own rows, so the app had two list designs: this
// one on the index pages and Item everywhere else. It now renders the shared
// row — the API is unchanged, the appearance is the same as every other list.

export type StatusTone = 'positive' | 'progress' | 'idle' | 'ended'

// Status colour comes from the theme's semantic tokens, not fixed palette
// colours, so it tracks light/dark and any future rebrand. `bg-primary` is the
// brand's "this is the live one" signal; muted tokens are the quiet states.
const TONE_DOT: Record<StatusTone, string> = {
  positive: 'bg-primary',
  progress: 'bg-muted-foreground',
  idle: 'bg-muted-foreground/30',
  ended: 'bg-destructive',
}

export type StageVariant = 'formation' | 'service'

const STAGE_STATUS: Record<StageVariant, Record<string, { label: string; tone: StatusTone }>> = {
  formation: {
    application: { label: 'Draft', tone: 'idle' },
    filing: { label: 'Filing', tone: 'progress' },
    completed: { label: 'Complete', tone: 'positive' },
  },
  service: {
    application: { label: 'Application', tone: 'idle' },
    payment: { label: 'Paid', tone: 'progress' },
    active: { label: 'Active', tone: 'positive' },
    ended: { label: 'Ended', tone: 'ended' },
  },
}

export function stageStatus(stage: string, variant: StageVariant): { label: string; tone: StatusTone } {
  return STAGE_STATUS[variant][stage] ?? { label: stage, tone: 'idle' }
}

type StatusProps = {
  label: string
  tone: StatusTone
}

export function ListStatus({ label, tone }: StatusProps) {
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5">
      <span aria-hidden className={cn('size-1.5 rounded-full', TONE_DOT[tone])} />
      <span className="text-xs text-muted-foreground">{label}</span>
    </span>
  )
}

type ListRowProps = {
  href: string
  title: string
  subtitle?: string
  icon?: ReactNode
  status?: StatusProps
}

export function ListRow({ href, title, subtitle, icon, status }: ListRowProps) {
  return (
    <Row
      href={href}
      icon={icon}
      title={title}
      description={subtitle}
      meta={status && <ListStatus label={status.label} tone={status.tone} />}
    />
  )
}

type ListProps = {
  children: ReactNode
  className?: string
}

export function List({ children, className }: ListProps) {
  return <Rows className={className}>{children}</Rows>
}

type ListEmptyProps = {
  icon: ReactNode
  title: string
  description: string
  action?: { label: string; href: string }
}

export function ListEmpty({ icon, title, description, action }: ListEmptyProps) {
  return (
    <Empty
      icon={icon}
      title={title}
      action={
        action && (
          <Button asChild size="sm">
            <Link href={action.href}>
              <Plus />
              {action.label}
            </Link>
          </Button>
        )
      }
    >
      {description}
    </Empty>
  )
}
